/**
 * Voltage Theme - Predictive Search
 * Search autocomplete functionality
 */

class PredictiveSearch {
  constructor(input, resultsContainer, options = {}) {
    this.input = input;
    this.resultsContainer = resultsContainer;
    this.options = {
      limit: options.limit || 6,
      types: options.types || 'product,article,page',
      debounceTime: options.debounceTime || 300,
      minChars: options.minChars || 2,
      ...options
    };

    this.isOpen = false;
    this.selectedIndex = -1;
    this.results = [];
    this.abortController = null;

    this.init();
  }

  init() {
    if (!this.input || !this.resultsContainer) return;

    // Input events
    this.input.addEventListener('input', Voltage.debounce((e) => {
      this.search(e.target.value);
    }, this.options.debounceTime));

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Focus events
    this.input.addEventListener('focus', () => {
      if (this.results.length > 0) {
        this.open();
      }
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.input.contains(e.target) && !this.resultsContainer.contains(e.target)) {
        this.close();
      }
    });
  }

  async search(query) {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < this.options.minChars) {
      this.close();
      return;
    }

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      this.showLoading();

      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(trimmedQuery)}&resources[type]=${this.options.types}&resources[limit]=${this.options.limit}`,
        { signal: this.abortController.signal }
      );

      const data = await response.json();
      this.results = this.processResults(data);
      this.render();
      this.open();

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.showError();
      }
    }
  }

  processResults(data) {
    const results = [];

    if (data.resources?.results?.products) {
      data.resources.results.products.forEach(product => {
        results.push({
          type: 'product',
          title: product.title,
          url: product.url,
          image: product.featured_image?.url || product.image,
          price: product.price,
          comparePrice: product.compare_at_price
        });
      });
    }

    if (data.resources?.results?.articles) {
      data.resources.results.articles.forEach(article => {
        results.push({
          type: 'article',
          title: article.title,
          url: article.url,
          image: article.featured_image?.url
        });
      });
    }

    if (data.resources?.results?.pages) {
      data.resources.results.pages.forEach(page => {
        results.push({
          type: 'page',
          title: page.title,
          url: page.url
        });
      });
    }

    return results;
  }

  render() {
    if (this.results.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="predictive-search__empty" style="padding: 2rem; text-align: center;">
          <p class="text-muted-foreground">No results found</p>
        </div>
      `;
      return;
    }

    const html = this.results.map((result, index) => {
      const isSelected = index === this.selectedIndex;

      if (result.type === 'product') {
        return `
          <a
            href="${result.url}"
            class="predictive-search__item predictive-search__item--product flex gap-4 p-4 hover:bg-surface ${isSelected ? 'is-selected bg-surface' : ''}"
            data-index="${index}"
          >
            <div class="predictive-search__image" style="width: 60px; flex-shrink: 0;">
              ${result.image
                ? `<img src="${result.image}" alt="${result.title}" width="60" height="60" style="width: 100%; height: auto; object-fit: cover;">`
                : `<div class="w-full aspect-square bg-surface"></div>`
              }
            </div>
            <div class="predictive-search__content flex-1">
              <span class="tech-text text-primary text-xs">PRODUCT</span>
              <p class="font-heading text-sm">${result.title}</p>
              <p class="text-sm">
                ${Voltage.formatMoney(result.price)}
                ${result.comparePrice && result.comparePrice > result.price
                  ? `<span class="line-through text-muted-foreground ml-2">${Voltage.formatMoney(result.comparePrice)}</span>`
                  : ''
                }
              </p>
            </div>
          </a>
        `;
      }

      if (result.type === 'article') {
        return `
          <a
            href="${result.url}"
            class="predictive-search__item predictive-search__item--article flex gap-4 p-4 hover:bg-surface ${isSelected ? 'is-selected bg-surface' : ''}"
            data-index="${index}"
          >
            ${result.image
              ? `<div class="predictive-search__image" style="width: 60px; flex-shrink: 0;">
                  <img src="${result.image}" alt="${result.title}" width="60" height="60" style="width: 100%; height: auto; object-fit: cover;">
                </div>`
              : ''
            }
            <div class="predictive-search__content flex-1">
              <span class="tech-text text-primary text-xs">ARTICLE</span>
              <p class="font-heading text-sm">${result.title}</p>
            </div>
          </a>
        `;
      }

      return `
        <a
          href="${result.url}"
          class="predictive-search__item predictive-search__item--page p-4 hover:bg-surface block ${isSelected ? 'is-selected bg-surface' : ''}"
          data-index="${index}"
        >
          <span class="tech-text text-primary text-xs">PAGE</span>
          <p class="font-heading text-sm">${result.title}</p>
        </a>
      `;
    }).join('');

    this.resultsContainer.innerHTML = `
      <div class="predictive-search__results">
        ${html}
      </div>
      <a href="/search?q=${encodeURIComponent(this.input.value)}" class="predictive-search__view-all p-4 text-center block border-t border-border hover:bg-surface">
        <span class="tech-text text-primary">VIEW ALL RESULTS</span>
      </a>
    `;

    // Click events on results
    this.resultsContainer.querySelectorAll('.predictive-search__item').forEach(item => {
      item.addEventListener('mouseenter', () => {
        this.selectedIndex = parseInt(item.dataset.index);
        this.updateSelection();
      });
    });
  }

  showLoading() {
    this.resultsContainer.innerHTML = `
      <div class="predictive-search__loading" style="padding: 2rem; text-align: center;">
        <div class="animate-pulse text-primary">SEARCHING...</div>
      </div>
    `;
    this.open();
  }

  showError() {
    this.resultsContainer.innerHTML = `
      <div class="predictive-search__error" style="padding: 2rem; text-align: center;">
        <p class="text-destructive">Search failed. Please try again.</p>
      </div>
    `;
  }

  handleKeydown(e) {
    if (!this.isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
        this.updateSelection();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection();
        break;

      case 'Enter':
        if (this.selectedIndex >= 0 && this.results[this.selectedIndex]) {
          e.preventDefault();
          window.location.href = this.results[this.selectedIndex].url;
        }
        break;

      case 'Escape':
        this.close();
        break;
    }
  }

  updateSelection() {
    this.resultsContainer.querySelectorAll('.predictive-search__item').forEach((item, i) => {
      item.classList.toggle('is-selected', i === this.selectedIndex);
      item.classList.toggle('bg-surface', i === this.selectedIndex);
    });
  }

  open() {
    this.isOpen = true;
    this.resultsContainer.hidden = false;
    this.resultsContainer.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.isOpen = false;
    this.selectedIndex = -1;
    this.resultsContainer.hidden = true;
    this.resultsContainer.setAttribute('aria-hidden', 'true');
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  const searchInputs = document.querySelectorAll('[data-predictive-search]');

  searchInputs.forEach(input => {
    const resultsId = input.dataset.predictiveSearch || 'predictive-search-results';
    const resultsContainer = document.getElementById(resultsId);

    if (resultsContainer) {
      new PredictiveSearch(input, resultsContainer);
    }
  });
});
